import { useStepperContext } from '@folio/components/layout/StepperLayout/useStepperContext';
import { PolicyTimeZoneProvider } from '@folio/utils/time-zone';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyStopReasons } from '@folio/utils/usePolicyStopReasons';
import { useMutation } from '@tanstack/react-query';
import { policyControllerStopPolicy } from 'korint-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { FunnelStepHeader } from '../../components/layout/FunnelStepHeader';
import { Checkbox } from '../../components/forms/checkbox';
import { StepperFooter } from '../../components/navigation/stepper-footer/stepper-footer';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ConsequencesPanel } from './ConsequencesPanel';
import { isEffectiveDateComplete, resolveEffectiveDate } from './effective-date';
import { StopFunnelGuard } from './StopFunnelGuard';
import { StopRequestSummary } from './StopRequestSummary';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function RecapStep({ firstStepPath }: { firstStepPath: string }) {
  return (
    <PolicyTimeZoneProvider>
      <StopFunnelGuard firstStepPath={firstStepPath}>
        {draft => <Recap reason={draft.reason} stopDate={draft.stopDate} />}
      </StopFunnelGuard>
    </PolicyTimeZoneProvider>
  );
}

function Recap({ reason, stopDate }: { reason: string; stopDate?: string }) {
  const { t } = useTranslation();
  const { policy, mutate } = usePolicyOnDashboard();
  const { next, afterEndPath } = useStepperContext();
  const navigate = useNavigate();
  const params = useParams();
  const [confirmed, setConfirmed] = useState(false);

  const { stopReasonConfigs } = usePolicyStopReasons({
    productId: policy.productId,
    policy: {
      startedAt: policy.startedAt,
      nextRenewalAt: policy.nextRenewalAt,
      suspendedAt: policy.suspendedAt,
    },
  });

  const reasonConfig = stopReasonConfigs.find(config => config.reason === reason);
  const effectiveDate = resolveEffectiveDate({ reason: reasonConfig, policy, stopDate });

  // mutate, not mutateAsync: StepperFooter cannot await onNext, and a rejected stop is already
  // surfaced by the shared MutationCache.
  const { mutate: stopPolicy, isPending } = useMutation({
    mutationFn: (details: { key: string; value: string }[]) =>
      policyControllerStopPolicy(policy.id, { reason, details }),
    onSuccess: async () => {
      await mutate();
      next();
    },
  });

  // Never submit before the config resolves the date key, nor without a date: falling back to now
  // would stop the policy immediately on a deep link into this step.
  const confirm = () => {
    if (!reasonConfig || !effectiveDate.at) {
      return;
    }
    // The backend reads the effective date under the reason's own appliedAtDate key.
    stopPolicy([{ key: reasonConfig.appliedAtDate ?? 'stopDate', value: effectiveDate.at }]);
  };

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('stopFunnel.recap.title')}
        subtitle={t('stopFunnel.recap.subtitle')}
      />

      <StopRequestSummary reason={reason} effectiveAt={effectiveDate.at} />

      <ConsequencesPanel effectiveDate={effectiveDate} productId={policy.productId} />

      <Card>
        <CardHeader>
          <CardTitle>{t('stopFunnel.recap.confirmationTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3">
            <Checkbox
              checked={confirmed}
              onCheckedChange={checked => setConfirmed(checked === true)}
            />
            <Text inline size="sm">
              {t('stopFunnel.recap.confirmation', { policy: policy.externalId })}
            </Text>
          </label>
        </CardContent>
      </Card>

      <StepperFooter
        onNext={confirm}
        nextButtonEnabled={confirmed && !!reasonConfig && isEffectiveDateComplete(effectiveDate)}
        loading={isPending}
        nextButtonText={t('stopFunnel.recap.submit')}
        secondaryAction={
          // Each step already persisted the draft; this only leaves the funnel.
          afterEndPath && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(generatePath(afterEndPath, params))}
            >
              {t('stopDraft.save')}
            </Button>
          )
        }
      />
    </Stack>
  );
}
